import { useCallback, useEffect, useState } from "react";
import { demoApi } from "@/api/client";
import type { BusinessEntity, Guid } from "@/api/types";
import { createDedupedFetcher } from "@/hooks/useDedupedFetch";

export type SelectedEntityStatus = "loading" | "ready" | "error";

const STORAGE_KEY = "easyfarm.entity.selected";

/**
 * ef-047-1 — the ONE entity switcher every screen that used to guess `entities[0]`
 * (`useReferenceData.ts`, `Fields.tsx`, `Grain.tsx`, `useLivestock.ts`) now reads from
 * instead, plus `EntitySwitcher.tsx` itself (mounted in `AppShell.tsx`'s topbar/entity
 * bar and `Dashboard.tsx`'s horizon row).
 *
 * MODULE-SCOPED EVENTTARGET, NOT REACT CONTEXT. This hook is mounted independently
 * across screens with no shared ancestor closer than the router root, and a handful of
 * them (the four call sites above) are hooks, not components — a Context provider would
 * have to wrap the whole app for something four unrelated leaves and two chrome
 * components read. A plain, module-level `EventTarget` lets every mounted instance in
 * this tab react the moment ANY of them calls `select()`, with no provider to thread.
 *
 * PERSISTED IN localStorage (`easyfarm.entity.selected`) so the choice survives a
 * reload — same accessor-guarded, degrade-quietly posture `offline/referenceCache.ts`
 * already uses for its own snapshot (Safari private browsing throws on access).
 */
const bus = new EventTarget();
const SELECTION_CHANGED = "selection-changed";

/**
 * De-dupes concurrent `GET /api/demo/entities` calls, never CACHES a resolved list.
 *
 * Every screen-level consumer (`Fields.tsx`, `Grain.tsx`, `useLivestock.ts`,
 * `useReferenceData.ts`) mounts its OWN `useSelectedEntity()` instance alongside the
 * chrome's own `EntitySwitcher` (this hook's own header remark on why — no shared
 * ancestor to lift a Context to). Two or more of those land in the SAME render wave on
 * most screens (the switcher in `AppShell`'s topbar plus the page's own instance), and
 * React 18 StrictMode's mount→cleanup→mount doubles each of THOSE again — four
 * concurrent requests for identical data, observed on `/farm/fields` alone. Sharing the
 * in-flight PROMISE (not the result) collapses that back to one real network round
 * trip: every consumer racing to mount within the same wave awaits the same request.
 *
 * ef-048-0c: the promise-sharing itself now lives in `useDedupedFetch.ts`'s
 * `createDedupedFetcher`, shared with `usePendingTime.ts` — deliberately still NOT a
 * persistent cache there either, so the NEXT independent mount (a later client-side
 * navigation, no full reload) always asks the server again rather than risking a stale
 * list after `Access.tsx`'s Entities section adds/renames/archives one elsewhere in the
 * same tab.
 */
const fetchEntitiesDeduped = createDedupedFetcher(() => demoApi.getEntities());

function readStored(): Guid | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(id: Guid): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // Quota, private mode, or storage disabled entirely — the selection still works for
    // every OTHER mounted instance in this tab via the bus below; it just will not
    // survive a reload. Not worth a visible error over.
  }
}

/** Default entity first (ef-047-2's `isDefault`), then every other active entity in the
 *  order the server returned them (`EntityRepository.GetEntitiesAsync`: `ORDER BY
 *  name`). At most one entity is ever `isDefault` (`entity_one_default_per_tenant`), so
 *  this is a stable partition, not a resort that could reorder the rest. */
function sortDefaultFirst(list: BusinessEntity[]): BusinessEntity[] {
  const defaultEntity = list.filter((entity) => entity.isDefault);
  const rest = list.filter((entity) => !entity.isDefault);
  return [...defaultEntity, ...rest];
}

/**
 * The stored choice wins only if it still names an ACTIVE entity for this tenant — an
 * id left over from an archived entity, a different tenant, or a corrupted value falls
 * through to the default entity, then the first active one (`list[0]` after
 * `sortDefaultFirst`), rather than resolving to nothing.
 */
function resolve(list: BusinessEntity[]): Guid | null {
  if (list.length === 0) return null;
  const stored = readStored();
  if (stored !== null && list.some((entity) => entity.id === stored)) return stored;
  return list[0]!.id;
}

export interface UseSelectedEntityResult {
  /** Active entities only, default first. */
  entities: BusinessEntity[];
  /** `null` while loading, or if the tenant genuinely has no active entity. */
  entityId: Guid | null;
  status: SelectedEntityStatus;
  /** Persists the choice and broadcasts it to every other mounted instance in this
   *  tab — live, with no reload/navigation in between. */
  select: (id: Guid) => void;
}

export function useSelectedEntity(): UseSelectedEntityResult {
  const [entities, setEntities] = useState<BusinessEntity[]>([]);
  const [entityId, setEntityId] = useState<Guid | null>(null);
  const [status, setStatus] = useState<SelectedEntityStatus>("loading");

  useEffect(() => {
    let cancelled = false;

    // Shared, de-duped fetch (see `fetchEntitiesDeduped` above) — this instance never
    // aborts it on its own cleanup, because another consumer mounted in the same wave
    // (the chrome's `EntitySwitcher` alongside this screen's own instance, most
    // commonly) may still be awaiting that exact promise. `cancelled` still guards the
    // setState calls below, same as before, so an instance that unmounted before the
    // shared request settles never touches state after the fact.
    async function load(): Promise<void> {
      setStatus("loading");
      try {
        const list = sortDefaultFirst(await fetchEntitiesDeduped());
        if (cancelled) return;
        setEntities(list);
        setEntityId(resolve(list));
        setStatus("ready");
      } catch {
        if (cancelled) return;
        setStatus("error");
      }
    }

    void load();

    // Another mounted instance (typically `EntitySwitcher`) just called `select()`.
    // Re-resolve against the stored id directly rather than refetching — every
    // instance already holds the same tenant's entity list, so the only thing that
    // changed is WHICH one is stored.
    function onSelectionChanged(): void {
      const stored = readStored();
      if (stored !== null) setEntityId(stored);
    }
    bus.addEventListener(SELECTION_CHANGED, onSelectionChanged);

    return () => {
      cancelled = true;
      bus.removeEventListener(SELECTION_CHANGED, onSelectionChanged);
    };
  }, []);

  const select = useCallback((id: Guid) => {
    writeStored(id);
    setEntityId(id);
    bus.dispatchEvent(new Event(SELECTION_CHANGED));
  }, []);

  return { entities, entityId, status, select };
}
